/**
 * Resolves the device mode once per session and hands it to the shell router.
 *
 * The resolution ORDER lives in `mode.ts` (pure, testable). This file supplies
 * the three inputs. The registered mode now comes from `GET /v1/devices/me`
 * (routes/qr.ts): the session's OWN device row, deliberately not gated behind
 * `device.view`, so a cashier or cook session finally learns the mode this
 * terminal was provisioned for. A 404 simply means "no device bound to this
 * session" and falls through to the local override — it is information, not an
 * error worth showing anyone.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { loadDeviceBinding, saveDeviceBinding } from '../lib/storage';
import type { DeviceBinding } from '../lib/storage';
import { resolveDeviceMode } from './mode';
import type { DeviceMode, ResolvedMode } from './mode';
import type { DeviceMeResponse } from '../lib/types';

/** The slice of the device row `GET /v1/devices/me` returns. */
export type RegisteredDevice = DeviceMeResponse['device'];

export interface DeviceModeState extends ResolvedMode {
  readonly binding: DeviceBinding | null;
  readonly device: RegisteredDevice | null;
  readonly resolving: boolean;
  /** Local override — how a manager repurposes a spare tablet mid-shift. */
  readonly setLocalMode: (mode: DeviceMode, stationId: string | null) => void;
  readonly setBinding: (binding: DeviceBinding | null) => void;
}

const DeviceModeContext = createContext<DeviceModeState | null>(null);

export function useDeviceMode(): DeviceModeState {
  const ctx = useContext(DeviceModeContext);
  if (ctx === null) throw new Error('useDeviceMode phải được dùng bên trong <DeviceModeProvider>.');
  return ctx;
}

export function DeviceModeProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const { api, me, signedIn } = useAuth();
  const [binding, setBindingState] = useState<DeviceBinding | null>(() => loadDeviceBinding());
  const [device, setDevice] = useState<RegisteredDevice | null>(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!signedIn || me === null) {
      setDevice(null);
      return undefined;
    }
    let alive = true;
    const controller = new AbortController();
    setResolving(true);
    // `GET /v1/devices/me` answers 404 when the session carries no deviceId.
    // Any failure degrades to the locally cached binding — never to an error.
    void (async () => {
      try {
        const response = await api.org.deviceMe({ signal: controller.signal });
        if (!alive) return;
        setDevice(response.device);
        // Refresh the local cache from the authoritative answer, so the NEXT
        // boot (possibly offline, possibly signed out) still picks the right
        // shell. Same shape the ActivationScreen writes.
        setBindingState((previous) => {
          const next: DeviceBinding = {
            deviceId: response.device.id,
            deviceName: response.device.deviceName,
            branchId: response.device.branchId,
            deviceMode: response.device.deviceMode,
            stationId: response.device.stationId,
          };
          if (
            previous !== null &&
            previous.deviceId === next.deviceId &&
            previous.deviceMode === next.deviceMode &&
            previous.stationId === next.stationId &&
            previous.branchId === next.branchId
          ) {
            return previous;
          }
          saveDeviceBinding(next);
          return next;
        });
      } catch {
        if (alive) setDevice(null);
      } finally {
        if (alive) setResolving(false);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [api, me, signedIn]);

  const setLocalMode = useCallback(
    (mode: DeviceMode, stationId: string | null) => {
      const next: DeviceBinding = {
        deviceId: binding?.deviceId ?? '',
        deviceName: binding?.deviceName ?? 'Thiết bị POS',
        branchId: binding?.branchId ?? me?.session.activeBranchId ?? null,
        deviceMode: mode,
        stationId,
      };
      saveDeviceBinding(next.deviceId === '' ? null : next);
      setBindingState(next);
    },
    [binding, me],
  );

  const setBinding = useCallback((next: DeviceBinding | null) => {
    saveDeviceBinding(next);
    setBindingState(next);
  }, []);

  const resolved = useMemo<ResolvedMode>(
    () =>
      resolveDeviceMode({
        registeredMode: device?.deviceMode ?? null,
        registeredStationId: device?.stationId ?? null,
        localMode: binding?.deviceMode ?? null,
        localStationId: binding?.stationId ?? null,
        permissions: me?.permissions ?? [],
        sessionStationId: me?.session.activeStationId ?? null,
      }),
    [binding, device, me],
  );

  const value = useMemo<DeviceModeState>(
    () => ({ ...resolved, binding, device, resolving, setLocalMode, setBinding }),
    [resolved, binding, device, resolving, setLocalMode, setBinding],
  );

  return <DeviceModeContext.Provider value={value}>{children}</DeviceModeContext.Provider>;
}
